import {
  DirectIndexType,
  GetAvailableIndexTypesDocument,
  GetAvailableIndexTypesQuery,
} from "@frec-js/common-client";
import { GetServerSideProps } from "next";
import Head from "next/head";
import { FC } from "react";

import { FootnoteAnchor, PricingFootnotes } from "../components/Anchor";
import { Disclaimers } from "../components/Disclaimers";
import { Footer } from "../components/Footer";
import { Header } from "../components/Header";
import { BrokerCheck } from "../sections/BrokerCheck";
import { PricingBorrowRates } from "../sections/pricing/PricingBorrowRates";
import { PricingFaq } from "../sections/pricing/PricingFaq";
import { PricingFees } from "../sections/pricing/PricingFees";
import { PricingHero } from "../sections/pricing/PricingHero";
import { PricingIndices } from "../sections/pricing/PricingIndices";
import { ZeroCommissionTrading } from "../sections/pricing/ZeroCommissionTrading";
import {
  getServerSidePropsHelper,
  ServerProps,
} from "../utils/serverSidePropsHelper";
import { createApolloClient } from "../utils/ssrApollo";

type PricingProps = ServerProps & {
  availableIndexTypes?: DirectIndexType[];
};

const Pricing: FC<PricingProps> = ({ isKnownUser, availableIndexTypes }) => {
  const title = "Pricing - Low-cost and transparent fees";

  const description =
    "We charge less than robo-advisors like Wealthfront, traditional brokerages like Fidelity, and wealth advisors.";

  return (
    <>
      <Head>
        <title>{title}</title>
        <link rel="canonical" href="https://frec.com/pricing" />
        <meta name="description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta
          property="og:image"
          content="https://frec.com/webstatic/images/share-img.png"
          key="og:image"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Head>
      <>
        <Header isKnownUser={isKnownUser} />
        <main>
          <PricingHero />
          <PricingFees />
          <PricingIndices availableIndexTypes={availableIndexTypes} />
          {/* <PricingMinimums /> */}
          <PricingBorrowRates />
          <ZeroCommissionTrading />
          <PricingFaq />
        </main>
        <BrokerCheck />
        <Footer />
        <Disclaimers
          disclaimers={[
            [
              FootnoteAnchor.DITaxAlphaDisclaimer,
              PricingFootnotes.withDITaxAlphaDisclaimer,
            ],
            [
              FootnoteAnchor.LowerFeesDisclaimer,
              PricingFootnotes.withLowerFeesDisclaimer,
            ],
            [
              FootnoteAnchor.ETFExpenseRatioDisclaimer,
              PricingFootnotes.withETFExpenseRatioDisclaimer,
            ],
            [
              FootnoteAnchor.VariableLTVDisclaimer,
              PricingFootnotes.withVariableLTVDisclaimer,
            ],
          ]}
        />
      </>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<PricingProps> = async ({
  req,
}) => {
  const props = getServerSidePropsHelper(req);

  let availableIndexTypes: DirectIndexType[] | undefined;

  try {
    const client = createApolloClient();
    const { data: availableIndexTypesResult } =
      await client.query<GetAvailableIndexTypesQuery>({
        query: GetAvailableIndexTypesDocument,
      });
    availableIndexTypes = availableIndexTypesResult?.getAvailableIndexTypes;
  } catch (e) {
    console.error("Unable to fetch available index types", e);
  }

  return Promise.resolve({
    props: {
      ...props,
      availableIndexTypes,
    },
  });
};

export default Pricing;
